import type { Amenity } from '@gyym/sdk'

/**
 * GYYM social media URLs
 */
export const GYYM_SAME_AS = [
  'https://x.com/gyymofficial',
  'https://www.instagram.com/gyym_official',
] as const

/**
 * Static base URL for JSON-LD (always production for SEO)
 */
export const PROD_SITE_BASE_URL = 'https://gyym.jp'

/**
 * GYYM organization info
 */
export const GYYM_ORGANIZATION = {
  '@type': 'Organization' as const,
  name: 'GYYM',
  url: PROD_SITE_BASE_URL,
  sameAs: GYYM_SAME_AS,
}

/**
 * Default price range for facilities
 */
export const DEFAULT_PRICE_RANGE = '¥4,000 - ¥12,000'

/**
 * Amenity key to Japanese label mapping
 */
export const AMENITY_LABELS: Record<keyof Amenity, string> = {
  locker: 'ロッカー',
  shower: 'シャワー',
  powderRoom: '化粧室',
  dryer: 'ドライヤー',
  shampoo: 'シャンプー',
  lotion: '化粧水',
  spa: 'スパ',
  sauna: 'サウナ',
  coldBath: '水風呂',
}

/**
 * Header navigation items for mainEntityOfPage
 */
export const WP_HEADER = {
  '@type': 'WPHeader' as const,
  cssSelector: 'aside',
  description: 'メインナビゲーションメニュー',
  hasPart: [
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'ログイン',
      url: `${PROD_SITE_BASE_URL}/login`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '新規登録',
      url: `${PROD_SITE_BASE_URL}/signup`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'ホーム',
      url: `${PROD_SITE_BASE_URL}/`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'お知らせ',
      url: `${PROD_SITE_BASE_URL}/notifications`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'GYYMについて',
      url: 'https://service.gyym.jp/teiki/about',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'ご利用料金',
      url: `${PROD_SITE_BASE_URL}/pricing`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'GYYM PASSについて',
      url: `${PROD_SITE_BASE_URL}/pass`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'GYYM WELLNESS JOURNAL',
      url: `${PROD_SITE_BASE_URL}/media/`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '現在地から探す',
      url: `${PROD_SITE_BASE_URL}/facilities?sortBy=distance&order=asc`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'エリアから探す',
      url: `${PROD_SITE_BASE_URL}/search?type=area`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '施設名から探す',
      url: `${PROD_SITE_BASE_URL}/search?type=name`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '条件から探す',
      url: `${PROD_SITE_BASE_URL}/search?type=condition`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'FAQ・お問い合わせ',
      url: 'https://support.gyym.jp/',
    },
  ],
}

/**
 * Footer navigation items for mainEntityOfPage
 */
export const WP_FOOTER = {
  '@type': 'WPFooter' as const,
  cssSelector: 'footer',
  hasPart: [
    {
      '@type': 'SiteNavigationElement' as const,
      name: '会社概要',
      url: `${PROD_SITE_BASE_URL}/company`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '特定商取引法に基づく表記',
      url: `${PROD_SITE_BASE_URL}/law`,
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '利用規約',
      url: 'https://service.gyym.jp/term',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'プライバシーポリシー',
      url: 'https://service.gyym.jp/policy',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'ソーシャルメディアポリシー',
      url: 'https://service.gyym.jp/snspolicy',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'ソーシャルメディアサービス利用規約',
      url: 'https://service.gyym.jp/snsterm',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: 'よくある質問',
      url: 'https://support.gyym.jp/',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '提携に関するお問い合わせ',
      url: 'https://service.gyym.jp/facility2?utm_source=gyym.jp&utm_campaign=footer_link',
    },
    {
      '@type': 'SiteNavigationElement' as const,
      name: '法人会員サービスに関するお問い合わせ',
      url: `${PROD_SITE_BASE_URL}/corp`,
    },
  ],
}

/**
 * mainEntityOfPage for header and footer navigation
 */
export const MAIN_ENTITY_OF_PAGE = [WP_HEADER, WP_FOOTER]
